import {computed, ComputedRef, nextTick, Ref, ref} from "vue";

/**
 * How many questions are left, as the line beside the replies says it.
 */
const count = (missing: string[]): string => {
    return missing.length > 1
        ? `${missing.length} questions sans réponse`
        : '1 question sans réponse';
};

/**
 * What holds the closing action of a scene while questions are left
 * unanswered: the replies stay clickable, and a click on them points at what
 * is missing instead of submitting — the rows ringed in red, the first one
 * scrolled to, and a line beside the replies saying what is left.
 *
 * Once shown, the rings follow the answers: a row answered drops its ring,
 * and the line goes when nothing is missing any more. A row is found by its
 * `data-question-ref` within the root of the scene, never across the thread,
 * where a frozen record may carry the same refs.
 *
 * @param missing The refs of the rows left unanswered, in the order shown.
 * @param root The root element of the scene.
 * @param describe What the line says of the rows left.
 */
export function useMissingAnswers(
    missing: ComputedRef<string[]>,
    root: Ref<HTMLElement | null>,
    describe: (missing: string[]) => string = count
) {
    const shown = ref(false);

    /**
     * Whether the closing action is held.
     */
    const blocked = computed(() => missing.value.length > 0);

    /**
     * The refs of the rows ringed in red.
     */
    const flagged = computed(() => shown.value ? missing.value : []);

    const warning = computed(() => flagged.value.length > 0 ? describe(flagged.value) : null);

    /**
     * Point at what is missing. The scroll waits for the line to land: the
     * thread follows its own growth to the bottom, and would otherwise snatch
     * the scroll back.
     */
    const flag = async () => {
        shown.value = true;

        await nextTick();
        await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

        const first = missing.value[0];

        if (first !== undefined) {
            root.value
                ?.querySelector(`[data-question-ref="${CSS.escape(first)}"]`)
                ?.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
    };

    return {blocked, flagged, warning, flag};
}
